import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import InputAdornment from '@mui/material/InputAdornment';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { format, addDays, parseISO } from 'date-fns';
import { showToastr } from '../../../components/ToastrMessage/Toast.helper';
import useTranslation from '../../../translations/use-translation';
import { DOCUMENT_GROUPS } from '../../../constants/documentList';
import seedMockCategoriesIfNeeded from '../CSCOwnerCategories/seedMockCategories';
import { getFieldConfig } from '../../../constants/serviceFieldConfig';
import './CSCOwnerAddService.styles.scss';


const QUALIFICATIONS = [
  '8th Pass',
  '10th Pass',
  '12th Pass',
  'ITI',
  'Diploma',
  'BA',
  'B.Com',
  'B.Sc',
  'BCA',
  'BBA',
  'B.Tech / BE',
  'B.Ed',
  'LLB',
  'MBBS',
  'B.Pharma',
  'Graduation',
  'MA',
  'M.Com',
  'M.Sc',
  'MCA',
  'MBA',
  'M.Tech / ME',
  'LLM',
  'MD',
  'Ph.D',
  'NA',
  'Others',
];

const INDIAN_STATES = [
  'All India',
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman & Nicobar', 'Chandigarh', 'Delhi', 'Jammu & Kashmir',
  'Ladakh', 'Lakshadweep', 'Puducherry',
];


const FEE_CATEGORIES = [
  { key: 'feeGeneral', label: 'General/Un-reserved (UR)' },
  { key: 'feeEWS',     label: 'EWS' },
  { key: 'feeOBC',     label: 'OBC' },
  { key: 'feeScSt',    label: 'SC/ST' },
];

const toDate = (str) => (str ? parseISO(str) : null);
const toStr  = (date) => (date && !isNaN(date) ? format(date, 'yyyy-MM-dd') : '');
const tomorrow = () => addDays(new Date(), 1);

const countWords = (text) =>
  text.trim() ? text.trim().split(/\s+/).length : 0;

const INITIAL = {
  serviceCategory:        '',
  customServiceCategory:  '',
  subCategory:            '',
  state:                '',
  serviceName:          '',
  serviceDescription:   '',
  qualifications:       [],
  requiredDocuments:    [],
  startDate:            '',
  endDate:              '',
  examDate:             '',
  examDateComingSoon:   false,
  admitCardDate:        '',
  admitCardComingSoon:  false,
  serviceImage:         null,
  serviceImagePreview:  null,
  minAge:               '',
  maxAge:               '',
  feeGeneral:           '',
  feeEWS:               '',
  feeOBC:               '',
  feeScSt:              '',
  cscCharge:            '',
  officialLink:         '',
  notificationFile:     null,
  notificationFileName: '',
};

const CSCOwnerAddService = () => {
  const translate = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit') || null;

  const fileInputRef = useRef(null);
  const notificationFileRef = useRef(null);
  const [form, setForm] = useState(INITIAL);
  const [editImageName, setEditImageName] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [customEntries, setCustomEntries] = useState([]);

  useEffect(() => {
    seedMockCategoriesIfNeeded();
    try {
      setCustomEntries(JSON.parse(localStorage.getItem('cscCustomCategories') || '[]'));
    } catch {
      setCustomEntries([]);
    }
  }, []);

  useEffect(() => {
    if (!editId) return;
    try {
      const services = JSON.parse(localStorage.getItem('cscServices') || '[]');
      const svc = services.find((s) => String(s.id) === String(editId));
      if (!svc) return;
      setForm({
        serviceCategory:        svc.serviceCategory        || '',
        customServiceCategory:  svc.customServiceCategory  || '',
        subCategory:            svc.subCategory            || '',
        state:               svc.state               || '',
        serviceName:         svc.serviceName         || '',
        serviceDescription:  svc.serviceDescription  || '',
        qualifications:      svc.qualifications      || [],
        requiredDocuments:   svc.requiredDocuments   || [],
        startDate:           svc.startDate           || '',
        endDate:             svc.endDate             || '',
        examDate:            svc.examDate            || '',
        examDateComingSoon:  svc.examDateComingSoon  || false,
        admitCardDate:       svc.admitCardDate       || '',
        admitCardComingSoon: svc.admitCardComingSoon || false,
        serviceImage:        null,
        serviceImagePreview: null,
        minAge:              svc.minAge              || '',
        maxAge:              svc.maxAge              || '',
        feeGeneral:          svc.feeGeneral          || '',
        feeEWS:              svc.feeEWS              || '',
        feeOBC:              svc.feeOBC              || '',
        feeScSt:             svc.feeScSt             || '',
        cscCharge:           svc.cscCharge           || '',
        officialLink:        svc.officialLink        || '',
        notificationFile:    svc.notificationFile    || null,
        notificationFileName: svc.notificationFileName || '',
      });
      if (svc.serviceImage) setEditImageName(svc.serviceImage);
    } catch (err) {
      console.error('Failed to load service for editing:', err);
    }
  }, [editId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'state') {
      setForm((p) => ({ ...p, state: value }));
    } else if (name === 'serviceCategory') {
      const cfg = getFieldConfig(value);
      setForm((p) => ({
        ...p,
        serviceCategory: value,
        customServiceCategory: '',
        subCategory: '',
        serviceName: '',
        ...(!cfg.qualifications && { qualifications: [] }),
        ...(!cfg.dates && { startDate: '', endDate: '' }),
        ...(!cfg.examDates && { examDate: '', examDateComingSoon: false, admitCardDate: '', admitCardComingSoon: false }),
        ...(!cfg.age && { minAge: '', maxAge: '' }),
      }));
    } else if (name === 'subCategory') {
      const isOther = value === 'Other Not in the List';
      setForm((p) => ({
        ...p,
        subCategory: value,
        serviceName: isOther ? '' : value,
      }));
    } else {
      setForm((p) => ({ ...p, [name]: value }));
    }
  };

  const handleQualification = (qual) =>
    setForm((p) => ({
      ...p,
      qualifications: p.qualifications.includes(qual)
        ? p.qualifications.filter((q) => q !== qual)
        : [...p.qualifications, qual],
    }));

  const handleDocument = (doc) =>
    setForm((p) => ({
      ...p,
      requiredDocuments: p.requiredDocuments.includes(doc)
        ? p.requiredDocuments.filter((d) => d !== doc)
        : [...p.requiredDocuments, doc],
    }));

  const handleToggle = (name) =>
    setForm((p) => ({ ...p, [name]: !p[name] }));

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm((p) => ({
      ...p,
      serviceImage:        file,
      serviceImagePreview: URL.createObjectURL(file),
    }));
  };

  const handleNotificationFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setForm((p) => ({
        ...p,
        notificationFile:     reader.result,
        notificationFileName: file.name,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleFee = (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (raw === '' || Number(raw) <= 100000)
      setForm((p) => ({ ...p, [e.target.name]: raw }));
  };

  const validate = () => {
    const e = {};
    const cfg = getFieldConfig(form.serviceCategory);

    if (!form.serviceCategory)
      e.serviceCategory = 'Service category is required';
    if (form.serviceCategory === 'Other' && !(form.customServiceCategory || '').trim())
      e.customServiceCategory = 'Please enter a category name';
    if (!form.subCategory)
      e.subCategory = 'Please select a sub category';
    if (!form.state)
      e.state = 'State is required';
    if (!(form.serviceName || '').trim())
      e.serviceName = 'Service name is required';
    if (!(form.serviceDescription || '').trim())
      e.serviceDescription = 'Description is required';
    else if (countWords(form.serviceDescription || '') > 100)
      e.serviceDescription = 'Description cannot exceed 100 words';

    if (cfg.dates) {
      if (!form.startDate)
        e.startDate = 'Start date is required';
      else if (form.startDate <= toStr(new Date()))
        e.startDate = 'Start date must be a future date';
      if (!form.endDate)
        e.endDate = 'End date is required';
      else if (form.startDate && form.endDate < form.startDate)
        e.endDate = 'End date must be after start date';
    }

    if (cfg.examDates) {
      if (!form.admitCardComingSoon && !form.admitCardDate)
        e.admitCardDate = 'Enter admit card date or mark as Coming Soon';
      else if (!form.admitCardComingSoon && form.admitCardDate && form.endDate && form.admitCardDate <= form.endDate)
        e.admitCardDate = 'Admit card date must be after the end date';
      if (!form.examDateComingSoon && !form.examDate)
        e.examDate = 'Enter exam date or mark as Coming Soon';
      else if (!form.examDateComingSoon && form.examDate && !form.admitCardComingSoon && form.admitCardDate && form.examDate <= form.admitCardDate)
        e.examDate = 'Exam date must be after the admit card date';
    }

    if (cfg.age) {
      if (form.minAge === '')
        e.minAge = 'Minimum age is required';
      else if (Number(form.minAge) < 0 || Number(form.minAge) > 100)
        e.minAge = 'Enter a valid age between 0 and 100';
      if (form.maxAge === '')
        e.maxAge = 'Maximum age is required';
      else if (Number(form.maxAge) < 0 || Number(form.maxAge) > 100)
        e.maxAge = 'Enter a valid age between 0 and 100';
      else if (form.minAge !== '' && form.maxAge !== '' && Number(form.maxAge) <= Number(form.minAge))
        e.maxAge = 'Maximum age must be greater than minimum age';
    }

    if (form.feeGeneral === '')  e.feeGeneral = 'Required (enter 0 if free)';
    if (form.feeEWS    === '')  e.feeEWS     = 'Required (enter 0 if free)';
    if (form.feeOBC    === '')  e.feeOBC     = 'Required (enter 0 if free)';
    if (form.feeScSt   === '')  e.feeScSt    = 'Required (enter 0 if free)';
    if (form.cscCharge === '')  e.cscCharge  = 'Required (enter 0 if no charge)';

    if (!(form.officialLink || '').trim())
      e.officialLink = 'Official link is required';
    else if (!/^https?:\/\/.+/.test(form.officialLink))
      e.officialLink = 'Enter a valid URL starting with http:// or https://';

    // Duplicate service check — only within owner-registered services
    const allServices = (() => {
      try { return JSON.parse(localStorage.getItem('cscServices') || '[]'); } catch { return []; }
    })();
    const ownerServices = allServices.filter((s) => s.addedByOwner);
    const othersOnly    = editId
      ? ownerServices.filter((s) => String(s.id) !== String(editId))
      : ownerServices;

    if (form.subCategory && form.subCategory !== 'Other Not in the List') {
      const dup = othersOnly.some(
        (s) => (s.subCategory || '').trim().toLowerCase() === form.subCategory.trim().toLowerCase()
      );
      if (dup) e.subCategory = 'You have already registered this service. Each service can only be registered once per center.';
    } else if ((form.serviceName || '').trim()) {
      const dup = othersOnly.some(
        (s) => (s.serviceName || '').trim().toLowerCase() === (form.serviceName || '').trim().toLowerCase()
      );
      if (dup) e.serviceName = 'You have already registered a service with this name.';
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const services = JSON.parse(localStorage.getItem('cscServices') || '[]');
      const fallbackImage  = editId ? editImageName : null;
      const imageValue     = form.serviceImage ? form.serviceImage.name : fallbackImage;
      const resolvedCategory = form.serviceCategory === 'Other'
        ? form.customServiceCategory.trim()
        : form.serviceCategory;
      const saveData = { ...form, serviceCategory: resolvedCategory };

      if (editId) {
        const idx = services.findIndex((s) => String(s.id) === String(editId));
        if (idx !== -1) {
          services[idx] = {
            ...services[idx],
            ...saveData,
            serviceImage:        imageValue,
            serviceImagePreview: null,
            addedByOwner:        true,
            updatedAt:           new Date().toISOString(),
          };
        }
        localStorage.setItem('cscServices', JSON.stringify(services));
        showToastr('success', 'Service updated successfully.');
        navigate('/csc-portal');
      } else {
        services.push({
          ...saveData,
          serviceImage:        imageValue,
          serviceImagePreview: null,
          id:                  Date.now(),
          addedByOwner:        true,
          createdAt:           new Date().toISOString(),
        });
        localStorage.setItem('cscServices', JSON.stringify(services));
        showToastr('success', 'Service registered successfully.');
        setForm(INITIAL);
        setEditImageName(null);
        setErrors({});
      }
    } catch (err) {
      console.error('Service save error:', err);
      showToastr('error', `Failed to ${editId ? 'update' : 'register'} service. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  const customCategoryNames = [...new Set(customEntries.map((e) => e.category))];

  const getSubCategoriesForCategory = (catName) => {
    if (!catName) return [];
    if (catName === 'Other') return [{ id: 'oth-other', name: 'Other Not in the List' }];
    const subs = customEntries
      .filter((e) => e.category === catName)
      .map((e) => ({ id: e.id, name: e.subCategory }));
    return [...subs, { id: 'oth-other', name: 'Other Not in the List' }];
  };

  const fieldConfig = getFieldConfig(form.serviceCategory);
  const wordCount = countWords(form.serviceDescription);

  const submitLabel = editId ? 'Update Service'  : 'Register Service';
  const savingLabel = editId ? 'Updating...'      : 'Registering...';

  const renderImageUpload = () => {
    if (form.serviceImagePreview) {
      return (
        <div className="cop-add-service__img-preview-row">
          <img
            src={form.serviceImagePreview}
            alt="Service preview"
            className="cop-add-service__img-preview"
          />
          <Button
            variant="outlined" size="small"
            className="cop-add-service__img-change-btn"
            onClick={() => fileInputRef.current?.click()}
          >
            Change Image
          </Button>
        </div>
      );
    }
    if (editImageName) {
      return (
        <div className="cop-add-service__img-existing">
          <span className="cop-add-service__img-existing-name">{editImageName}</span>
          <Button
            variant="outlined" size="small"
            className="cop-add-service__img-change-btn"
            onClick={() => fileInputRef.current?.click()}
          >
            Replace Image
          </Button>
        </div>
      );
    }
    return (
      <Button
        variant="outlined" size="small"
        className="cop-add-service__img-upload-btn"
        onClick={() => fileInputRef.current?.click()}
      >
        Choose Image (PNG, JPG, JPEG)
      </Button>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
    <div className="cop-add-service">
      <div className="cop-add-service__header">
        {editId && (
          <button
            type="button"
            className="cop-add-service__back-btn"
            onClick={() => navigate(-1)}
          >
            <ArrowBackIosNewIcon style={{ fontSize: '0.85rem' }} />
            Back
          </button>
        )}
        <div className="cop-add-service__header-text">
          <h2 className="cop-add-service__title">
            {editId ? 'Edit Service' : translate('CSC_REGISTER_SERVICE_HEADING')}
          </h2>
          <span className="cop-add-service__subtitle">
            {editId
              ? 'Update the details for this service'
              : translate('CSC_REGISTER_SERVICE_SUBTITLE')}
          </span>
        </div>
      </div>

      {/* ── Two-column body ────────────────────────────────────── */}
      <div className="cop-add-service__two-col">

        {/* ── LEFT column ─────────────────────────────────────── */}
        <div className="cop-add-service__col">

          {/* Section 1: Basic Info */}
          <div className="cop-add-service__section">
            <h3 className="cop-add-service__section-title">Basic Information</h3>

            <div className="cop-add-service__row">
              <TextField
                select size="small" variant="outlined" fullWidth
                label="State"
                name="state"
                value={form.state}
                onChange={handleChange}
                error={!!errors.state}
                helperText={errors.state}
              >
                {INDIAN_STATES.map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </TextField>
            </div>

            <div className="cop-add-service__row">
              <TextField
                select size="small" variant="outlined" fullWidth
                label="Service Category"
                name="serviceCategory"
                value={form.serviceCategory}
                onChange={handleChange}
                error={!!errors.serviceCategory}
                helperText={errors.serviceCategory || (customCategoryNames.length === 0 ? 'Add categories in My Categories first' : '')}
              >
                {customCategoryNames.map((catName) => (
                  <MenuItem key={catName} value={catName}>{catName}</MenuItem>
                ))}
                <MenuItem value="Other">Other</MenuItem>
              </TextField>
            </div>

            {form.serviceCategory === 'Other' && (
              <div className="cop-add-service__row">
                <TextField
                  size="small" variant="outlined" fullWidth
                  label="Category Name"
                  name="customServiceCategory"
                  value={form.customServiceCategory}
                  onChange={handleChange}
                  error={!!errors.customServiceCategory}
                  helperText={errors.customServiceCategory}
                  placeholder="Enter your service category..."
                  autoFocus
                />
              </div>
            )}

            <div className="cop-add-service__row">
              <TextField
                select size="small" variant="outlined" fullWidth
                label="Sub Category (Service)"
                name="subCategory"
                value={form.subCategory}
                onChange={handleChange}
                disabled={!form.serviceCategory}
                error={!!errors.subCategory}
                helperText={errors.subCategory || (!form.serviceCategory ? 'Select a category first' : '')}
              >
                {getSubCategoriesForCategory(form.serviceCategory).map((item) => (
                  <MenuItem key={item.id} value={item.name}>{item.name}</MenuItem>
                ))}
              </TextField>
            </div>

            {form.subCategory === 'Other Not in the List' && (
              <div className="cop-add-service__row">
                <TextField
                  size="small" variant="outlined" fullWidth
                  label="Service Name"
                  name="serviceName"
                  value={form.serviceName}
                  onChange={handleChange}
                  error={!!errors.serviceName}
                  helperText={errors.serviceName}
                  placeholder="Enter the service name..."
                  autoFocus
                />
              </div>
            )}

            <div className="cop-add-service__row">
              <TextField
                multiline rows={4}
                size="small" variant="outlined" fullWidth
                label="Service Description"
                name="serviceDescription"
                value={form.serviceDescription}
                onChange={handleChange}
                error={!!errors.serviceDescription}
                helperText={errors.serviceDescription || `${wordCount}/100 words`}
                placeholder="Describe the service in up to 100 words..."
              />
            </div>
          </div>

          {/* Section 3: Important Dates */}
          {(fieldConfig.dates || fieldConfig.examDates) && (
          <div className="cop-add-service__section">
            <h3 className="cop-add-service__section-title">Important Dates</h3>

            {fieldConfig.dates && (
            <div className="cop-add-service__row cop-add-service__row--2col">
              <DatePicker
                label="Start Date"
                value={toDate(form.startDate)}
                onChange={(d) => setForm((p) => ({ ...p, startDate: toStr(d) }))}
                minDate={tomorrow()}
                slotProps={{
                  textField: {
                    size: 'small', variant: 'outlined', fullWidth: true,
                    error: !!errors.startDate, helperText: errors.startDate,
                  },
                }}
              />
              <DatePicker
                label="End Date"
                value={toDate(form.endDate)}
                onChange={(d) => setForm((p) => ({ ...p, endDate: toStr(d) }))}
                minDate={toDate(form.startDate) || tomorrow()}
                slotProps={{
                  textField: {
                    size: 'small', variant: 'outlined', fullWidth: true,
                    error: !!errors.endDate, helperText: errors.endDate,
                  },
                }}
              />
            </div>
            )}

            {fieldConfig.examDates && (
            <div className="cop-add-service__row cop-add-service__row--2col">
              <div className="cop-add-service__date-cell">
                <DatePicker
                  label="Exam Date"
                  value={toDate(form.examDate)}
                  onChange={(d) => setForm((p) => ({ ...p, examDate: toStr(d) }))}
                  disabled={form.examDateComingSoon}
                  slotProps={{
                    textField: {
                      size: 'small', variant: 'outlined', fullWidth: true,
                      error: !!errors.examDate, helperText: errors.examDate,
                    },
                  }}
                />
                <FormControlLabel
                  className="cop-add-service__coming-soon-check"
                  control={<Checkbox size="small" checked={form.examDateComingSoon} onChange={() => handleToggle('examDateComingSoon')} />}
                  label="Coming Soon"
                />
              </div>
              <div className="cop-add-service__date-cell">
                <DatePicker
                  label="Admit Card Available Date"
                  value={toDate(form.admitCardDate)}
                  onChange={(d) => setForm((p) => ({ ...p, admitCardDate: toStr(d) }))}
                  disabled={form.admitCardComingSoon}
                  slotProps={{
                    textField: {
                      size: 'small', variant: 'outlined', fullWidth: true,
                      error: !!errors.admitCardDate, helperText: errors.admitCardDate,
                    },
                  }}
                />
                <FormControlLabel
                  className="cop-add-service__coming-soon-check"
                  control={<Checkbox size="small" checked={form.admitCardComingSoon} onChange={() => handleToggle('admitCardComingSoon')} />}
                  label="Coming Soon"
                />
              </div>
            </div>
            )}
          </div>
          )}

          {/* Section 4: Service Image */}
          <div className="cop-add-service__section">
            <h3 className="cop-add-service__section-title">Service Image</h3>
            <input ref={fileInputRef} type="file" accept="image/png,image/jpg,image/jpeg" style={{ display: 'none' }} onChange={handleImage} />
            {renderImageUpload()}
          </div>

        </div>{/* end left col */}

        {/* ── RIGHT column ────────────────────────────────────── */}
        <div className="cop-add-service__col">

          {/* Section 2: Qualification */}
          {fieldConfig.qualifications && (
          <div className="cop-add-service__section">
            <h3 className="cop-add-service__section-title">
              Qualification <span className="cop-add-service__optional">(Optional)</span>
            </h3>
            <FormGroup row className="cop-add-service__qual-group">
              {QUALIFICATIONS.map((q) => (
                <FormControlLabel
                  key={q}
                  className="cop-add-service__qual-label"
                  control={<Checkbox size="small" checked={form.qualifications.includes(q)} onChange={() => handleQualification(q)} />}
                  label={q}
                />
              ))}
            </FormGroup>
          </div>
          )}

          {/* Section 5: Age Criteria */}
          {fieldConfig.age && (
          <div className="cop-add-service__section">
            <h3 className="cop-add-service__section-title">Age Criteria</h3>
            <div className="cop-add-service__row cop-add-service__row--2col">
              <TextField
                size="small" variant="outlined" fullWidth
                label="Minimum Age" name="minAge" type="number"
                value={form.minAge} onChange={handleChange}
                error={!!errors.minAge} helperText={errors.minAge}
                inputProps={{ min: 0, max: 100 }}
              />
              <TextField
                size="small" variant="outlined" fullWidth
                label="Maximum Age" name="maxAge" type="number"
                value={form.maxAge} onChange={handleChange}
                error={!!errors.maxAge} helperText={errors.maxAge}
                inputProps={{ min: 0, max: 100 }}
              />
            </div>
            <p className="cop-add-service__section-hint">Age relaxation as per rules (refer to official notification)</p>
          </div>
          )}

          {/* Section 6: Application Fees */}
          <div className="cop-add-service__section">
            <h3 className="cop-add-service__section-title">Application Fees</h3>
            <p className="cop-add-service__section-hint">Government application fees (₹0 to ₹1,00,000) — varies by applicant category</p>
            <div className="cop-add-service__fees-grid">
              {FEE_CATEGORIES.map(({ key, label }) => (
                <div key={key} className="cop-add-service__fee-row">
                  <span className="cop-add-service__fee-label">{label}</span>
                  <TextField
                    size="small" variant="outlined"
                    name={key} value={form[key]} onChange={handleFee} placeholder="0"
                    error={!!errors[key]} helperText={errors[key]}
                    inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                    InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                    className="cop-add-service__fee-input"
                  />
                </div>
              ))}
            </div>
            <div className="cop-add-service__csc-charge-row">
              <h4 className="cop-add-service__csc-charge-title">CSC Processing Charge</h4>
              <p className="cop-add-service__section-hint" style={{ marginBottom: 8 }}>Your service centre's processing fee charged to the applicant (enter 0 if free)</p>
              <div className="cop-add-service__fee-row">
                <span className="cop-add-service__fee-label">CSC Charge (all categories)</span>
                <TextField
                  size="small" variant="outlined"
                  name="cscCharge" value={form.cscCharge} onChange={handleFee} placeholder="0"
                  error={!!errors.cscCharge} helperText={errors.cscCharge}
                  inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                  className="cop-add-service__fee-input"
                />
              </div>
            </div>
          </div>

          {/* Section 8: Official Links */}
          <div className="cop-add-service__section">
            <h3 className="cop-add-service__section-title">Official Links</h3>
            <div className="cop-add-service__row">
              <TextField
                size="small" variant="outlined" fullWidth
                label="Official Website" name="officialLink"
                value={form.officialLink} onChange={handleChange}
                error={!!errors.officialLink} helperText={errors.officialLink}
                placeholder="https://..."
              />
            </div>
            <div className="cop-add-service__row" style={{ marginTop: '12px' }}>
              <p className="cop-add-service__section-hint" style={{ margin: '0 0 8px' }}>
                Notification PDF <span className="cop-add-service__optional">(Optional)</span>
              </p>
              <input ref={notificationFileRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={handleNotificationFile} />
              {form.notificationFileName ? (
                <div className="cop-add-service__img-existing">
                  <span className="cop-add-service__img-existing-name">{form.notificationFileName}</span>
                  <Button variant="outlined" size="small" className="cop-add-service__img-change-btn" onClick={() => notificationFileRef.current?.click()}>Replace</Button>
                  <Button variant="outlined" size="small" color="error" style={{ marginLeft: '8px' }} onClick={() => setForm((p) => ({ ...p, notificationFile: null, notificationFileName: '' }))}>Remove</Button>
                </div>
              ) : (
                <Button variant="outlined" size="small" className="cop-add-service__img-upload-btn" onClick={() => notificationFileRef.current?.click()}>Upload Notification (PDF)</Button>
              )}
            </div>
          </div>

        </div>{/* end right col */}
      </div>{/* end two-col */}

      {/* ── Section 7: Required Documents (full width) ────────── */}
      <div className="cop-add-service__section">
        <h3 className="cop-add-service__section-title">
          Required Documents <span className="cop-add-service__optional">(Optional)</span>
        </h3>
        <p className="cop-add-service__section-hint">Select all documents the applicant must bring / upload for this service</p>
        {DOCUMENT_GROUPS.map(({ group, docs }) => (
          <div key={group} className="cop-add-service__doc-group">
            <span className="cop-add-service__doc-group-label">{group}</span>
            <FormGroup row className="cop-add-service__qual-group">
              {docs.map((doc) => (
                <FormControlLabel
                  key={doc}
                  className="cop-add-service__qual-label"
                  control={<Checkbox size="small" checked={form.requiredDocuments.includes(doc)} onChange={() => handleDocument(doc)} />}
                  label={doc}
                />
              ))}
            </FormGroup>
          </div>
        ))}
      </div>

      {/* ── Submit ─────────────────────────────────────────────── */}
      <div className="cop-add-service__actions">
        <Button
          variant="contained" disableElevation
          className="cop-add-service__submit-btn"
          onClick={handleSubmit}
          disabled={saving}
          endIcon={saving && <CircularProgress color="inherit" size={16} />}
        >
          {saving ? savingLabel : submitLabel}
        </Button>
      </div>
    </div>
    </LocalizationProvider>
  );
};

export default CSCOwnerAddService;
